import { API, Auth, graphqlOperation } from "aws-amplify" //, API, graphqlOperation

import {
  createAssociation as CreateAssociation,
  createAssociationsUsers as CreateAssociationsUsers,
  createUser as CreateUser,
} from "../graphqlCustom/mutations"
import { getAssociation as GetAssociation, getUser as GetUser } from "../graphqlCustom/queries"

// import {
//   // createAssociation as CreateAssociation,
//   // createAssociationsUsers as CreateAssociationsUsers,
//   createUser as CreateUser,
//   // deleteInvite as DeleteInvite,
// } from "../graphqlCustom/mutations"

/**

Workflow:
- User adds info, create cognito account
- Confirm email field
- Continue

- When user logs in
  - if cognito, but not verified, require verification
    - show verification field
    - also button to resend verification email
    - back to onboarding minus email (cognito already exists)
  - if not db user, you need to finish creating your account:
    - back to onboarding minus email (cognito already exists)
  - if db user, but no association
    - You aren't currently a member of an association, create one
  - if db user, and invite waiting for email:
    - You have an invite waiting, accept invite?

- When invite sent
  - Log in and accept invite or
  - Only show userinfo
  - create account
  - confirm email
  - join association

 */

const requiredUserProperties = ["email", "firstName", "lastName"]

export const emailAvailable = async (email) => {
  // adapted from @herri16's solution: https://github.com/aws-amplify/amplify-js/issues/1067#issuecomment-436492775
  try {
    await Auth.confirmSignUp(email, "000000", {
      // If set to False, the API will throw an AliasExistsException error if the phone number/email used already exists as an alias with a different user
      forceAliasCreation: false,
    })
    // this should always throw an error of some kind, but if for some reason this succeeds then the user probably exists.
    return false
  } catch (error) {
    switch (error.code) {
      case "UserNotFoundException":
        return true
      // Email already exists
      case "NotAuthorizedException":
        return false
      case "AliasExistsException":
        return false
      case "CodeMismatchException":
        return false
      case "ExpiredCodeException":
        return false
      default:
        return false
    }
  }
}

export const logIn = async (email, password) => {
  const user = await Auth.signIn(email, password)
  if (user) {
    const cognitoUser = formatCognitoUser(user)
    const dbUser = await fetchDbUser(cognitoUser.id)
    if (dbUser) {
      return {
        ...dbUser,
        ...cognitoUser,
        isLoggedIn: true,
      }
    }
  }
  return user
}

export const isLoggedIn = async () => {
  const cognitoUser = await fetchCognitoUser()
  return cognitoUser ? true : false
}

export const fetchUser = async (user) => {
  const cognitoUser = await fetchCognitoUser()
  if (cognitoUser) {
    if (user && user.id === cognitoUser.id) {
      return {
        ...user,
        ...cognitoUser,
        isLoggedIn: true,
      }
    } else {
      const dbUser = await fetchDbUser(cognitoUser.id)
      if (dbUser) {
        return {
          ...dbUser,
          ...cognitoUser,
          isLoggedIn: true,
        }
      }
      // } else {
      //   return cognitoUser
      // }
    }
  }
  return undefined
}

export const formatCognitoUser = (user) => {
  if (user && user.attributes) {
    return {
      displayName: `${user.attributes.given_name} ${user.attributes.family_name}`,
      email: user.attributes.email,
      email_verified: user.attributes.email_verified,
      firstName: user.attributes.given_name,
      id: user.attributes.sub,
      isLoggedIn: false,
      lastName: user.attributes.family_name,
    }
  }
  return user
}

export const fetchCognitoUser = async () => {
  const user = await Auth.currentUserInfo()
  return formatCognitoUser(user)
}

export const fetchDbUser = async (id) => {
  const user = await API.graphql(graphqlOperation(GetUser, { id: id }))
  if (user && user.data.getUser) {
    return {
      associations: user.data.getUser.associations.items,
      avatar: "",
      displayName: `${user.data.getUser.firstName} ${user.data.getUser.lastName}`,
      email: user.data.getUser.email,
      firstName: user.data.getUser.firstName,
      id: user.data.getUser.id,
      lastName: user.data.getUser.lastName,
      phone: user.data.getUser.phone,
    }
  } else {
    // The database user may not exist, so send them back through sign up
    Auth.signOut()
    return null
  }
}

export const fetchAssociation = async (id) => {
  const association = await API.graphql(graphqlOperation(GetAssociation, { id: id }))
  if (association && association.data.getAssociation) {
    return association
  } else {
    return null
  }
}

export const createAssociation = async (association, user, title = null, unit = null) => {
  const newAssociation = await API.graphql(
    graphqlOperation(CreateAssociation, { input: association })
  )
  const associationId = newAssociation.data.createAssociation.id

  if (associationId && user.id) {
    // Create connection
    addAssociationToUser(associationId, user.id, "BOARD", title, unit)
    return associationId
  } else {
    throw new Error("Unable to create association")
  }
}

export const addAssociationToUser = async (
  associationId,
  userId,
  role = null,
  title = null,
  unit = null
) => {
  // Create connection
  const newJoin = await API.graphql(
    graphqlOperation(CreateAssociationsUsers, {
      input: {
        associationsUsersUserId: userId,
        associationsUsersAssociationId: associationId,
        role: role || "MEMBER",
        title,
        unit,
      },
    })
  )
  return newJoin
}

export const createCognitoUser = async (user) => {
  if (
    !requiredUserProperties.every(function (property) {
      return property in user
    })
  ) {
    throw new Error("Missing required information")
  }
  try {
    const newUser = await Auth.signUp({
      username: user.email,
      password: user.password,
      attributes: {
        address: user.unit,
        email: user.email,
        given_name: user.firstName,
        family_name: user.lastName,
        profile: user.position,
      },
    })
    return newUser
  } catch (error) {
    return error
  }
}

// Add "invite"
export const createDbUser = async (user) => {
  if (
    !requiredUserProperties.every(function (property) {
      return property in user
    })
  ) {
    throw new Error("Missing required information")
  }
  const dbUser = await API.graphql(graphqlOperation(CreateUser, { input: user }))
  if (dbUser.data.createUser) {
    return dbUser.data.createUser
  } else {
    throw new Error("Unable to finish creating user")
  }
}

export const createAccount = (association, user, code) => {}
